import { addDays, format, isValid, parseISO, startOfDay } from "date-fns";
import {
  DEFAULT_DELIVERY_WINDOW_SPAN_DAYS,
  getEarliestDeliveryDate,
  latestDeliveryDateFromEarliest,
  MIN_DELIVERY_LEAD_DAYS,
} from "@shared/formats/calendar-date";

export interface DeliveryWindow {
  start: string;
  end: string;
}

const MIN_PICKUP_LEAD_DAYS = 1;

export function earliestSelectableDeliveryDate(
  leadTimeDays = MIN_DELIVERY_LEAD_DAYS,
  now: number = Date.now(),
): Date {
  return parseCalendarDate(getEarliestDeliveryDate(leadTimeDays, now));
}

export function earliestSelectablePickupDate(): Date {
  return startOfDay(addDays(new Date(), MIN_PICKUP_LEAD_DAYS));
}

export function earliestStaffSelectableDate(): Date {
  return startOfDay(new Date());
}

export function latestSelectableDeliveryDate(now: number = Date.now()): Date {
  return latestSelectableDeliveryDateForLeadTime(MIN_DELIVERY_LEAD_DAYS, now);
}

export function latestSelectableDeliveryDateForLeadTime(
  leadTimeDays: number,
  now: number = Date.now(),
): Date {
  return latestSelectableDeliveryDateFromEarliest(
    earliestSelectableDeliveryDate(leadTimeDays, now),
    now,
  );
}

export function latestSelectableDeliveryDateFromEarliest(
  earliestDate: Date,
  now: number = Date.now(),
): Date {
  return parseCalendarDate(latestDeliveryDateFromEarliest(toDateKey(earliestDate), now));
}

export function defaultDeliveryWindow(
  leadTimeDays = MIN_DELIVERY_LEAD_DAYS,
  now: number = Date.now(),
): DeliveryWindow {
  const start = earliestSelectableDeliveryDate(leadTimeDays, now);
  return {
    start: toDateKey(start),
    end: toDateKey(addDays(start, DEFAULT_DELIVERY_WINDOW_SPAN_DAYS)),
  };
}

export function toDateKey(date: Date): string {
  return format(startOfDay(date), "yyyy-MM-dd");
}

export function parseDateKey(key: string): Date | null {
  const parsed = parseISO(`${key}T12:00:00`);
  return isValid(parsed) ? parsed : null;
}

export const DELIVERY_WINDOW_PARAM = "window";

export function serializeDeliveryWindow(window: DeliveryWindow): string {
  return `${window.start}_${window.end}`;
}

export function parseDeliveryWindowParam(
  value: string | null | undefined,
  leadTimeDays = MIN_DELIVERY_LEAD_DAYS,
  now: number = Date.now(),
): DeliveryWindow | null {
  if (!value) return null;
  const [start, end] = value.split("_");
  if (!start || !end) return null;
  const window = { start, end };
  if (!isValidDeliveryWindow(window)) return null;
  const earliest = toDateKey(earliestSelectableDeliveryDate(leadTimeDays, now));
  const latest = toDateKey(
    latestSelectableDeliveryDateForLeadTime(leadTimeDays, now),
  );
  return start >= earliest && end <= latest ? window : null;
}

export function refreshDeliveryWindow(
  window: DeliveryWindow,
  leadTimeDays: number,
  now: number = Date.now(),
): DeliveryWindow {
  return (
    parseDeliveryWindowParam(
      serializeDeliveryWindow(window),
      leadTimeDays,
      now,
    ) ?? defaultDeliveryWindow(leadTimeDays, now)
  );
}

export function refreshPreferredDeliveryDate(
  selectedDate: string | null,
  leadTimeDays: number,
  now: number = Date.now(),
): string {
  const earliestDate = getEarliestDeliveryDate(leadTimeDays, now);
  return selectedDate && selectedDate >= earliestDate
    ? selectedDate
    : earliestDate;
}

function parseCalendarDate(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

export function isValidDeliveryWindow(window: DeliveryWindow | null): boolean {
  if (!window) return false;
  const start = parseDateKey(window.start);
  const end = parseDateKey(window.end);
  if (!start || !end) return false;
  return end.getTime() >= start.getTime();
}

export function formatDeliveryWindow(window: DeliveryWindow | null): string {
  if (!isValidDeliveryWindow(window) || !window) return "";
  const start = parseDateKey(window.start);
  const end = parseDateKey(window.end);
  if (!start || !end) return "";
  if (start.getTime() === end.getTime()) return format(start, "MMM d");
  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  return sameMonth
    ? `${format(start, "MMM d")} – ${format(end, "d")}`
    : `${format(start, "MMM d")} – ${format(end, "MMM d")}`;
}
